import { EvaEndpoint, EvaService } from "@springtree/eva-sdk-core-service";
import {
  createServiceDefinition,
  GetProductAvailability,
  GetProductDetailResponse,
  GetProductsResponse,
  IEvaServiceDefinition,
  Login,
  OrdersDtoOrderLineDto,
  SearchFilterModel,
  SearchProducts,
  SortFieldDescriptor,
  StockGetProductAvailability_Product,
  SvcGetApplicationConfiguration,
  SvcGetProductAvailability,
  SvcGetProductDetail,
  SvcGetProducts,
  SvcListOrganizationUnitSummaries,
  SvcListShopsByProximity,
  SvcPrefigureDiscounts,
  SvcSearchProducts,
  SvcSetShippingMethod,
} from "@springtree/eva-services-core";

import Logger from "~/services/logger/logger";
import { Coords } from "~/shared/coords.class";
import Constants from "~/utils/constants";
import invariant from "~/utils/invariant";

import { KIKOApplicationConfiguration } from "../../global";

export default class Eva {
  static DEFAULT_CATEGORY_SORT: SortFieldDescriptor[] = [{ FieldName: "display_value", Direction: 0 }];
  private static DEFAULT_SEARCH_PRODUCTS_REQUEST = {
    IncludedFields: Constants.QUERY_INCLUDED_FIELDS,
    Options: {
      IncludePrefigureDiscounts: true,
      AvailabilityOptions: {
        Delivery: {
          AvailabilityDate: true,
        },
        Pickup: {
          IncludePickupOrganizationUnits: false,
          IncludeOrganizationUnitsWithoutStock: true,
        },
      },
    },
    Filters: {
      is_sellable: {
        Values: [true],
        IncludeMissing: false,
      },
      display_price: {
        From: "0",
        IncludeMissing: false,
      },
    },
  } satisfies SearchProducts;

  static async getOrganizationUnitId(locale: string) {
    const eva = new Eva({ locale });
    const units = await eva.listOrganizationUnitSummaries();
    const country = locale.split("-")[1];
    const unit = units.find((w) => w.CountryID.toLowerCase() === country.toLowerCase());

    invariant(unit, `Could not find unit for country ${country}.`);

    return unit.ID;
  }

  protected locale: string | undefined;
  protected organizationUnitId: string | undefined;
  protected userToken: string | undefined;
  protected onTokenExpired?: () => Promise<string | undefined>;

  constructor({
    locale,
    organizationUnitId,
    userToken,
    onTokenExpired,
  }: {
    locale?: string;
    organizationUnitId?: string;
    userToken?: string;
    onTokenExpired?: () => Promise<string | undefined>;
  } = {}) {
    this.locale = locale;
    this.organizationUnitId = organizationUnitId;
    this.userToken = userToken;
    this.onTokenExpired = onTokenExpired;
  }

  static buildSSOLoginPayload(id_token: string, provider: string): Login {
    return {
      AsEmployee: false,
      SelectFirstOrganizationUnit: true,
      CustomAuthenticatorType: "OpenID",
      CustomAuthenticateData: {
        id_token,
        provider,
      },
    };
  }

  async getGroupedProductSlugs(page = 1) {
    const response = await this.getService(SvcSearchProducts, {
      Page: page,
      PageSize: 100,
      IncludedFields: ["slug", "display_value", "logical_level_hierarchy.product_id"],
      Filters: {
        slug: {
          IncludeMissing: false,
        },
        logical_level: {
          Values: ["color"],
          IncludeMissing: true,
        },
        is_sellable: {
          Values: [true],
          IncludeMissing: false,
        },
      },
      Sort: [
        { FieldName: "logical_level_hierarchy.product_id", Direction: 0 },
        { FieldName: "color", Direction: 0 },
      ],
    });

    return response!;
  }

  async getProductsByIds(
    parentIds: string[],
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const response = await this.getService(SvcSearchProducts, {
      ...Eva.DEFAULT_SEARCH_PRODUCTS_REQUEST,
      Page: page,
      PageSize: pageSize,
      Sort: sort,

      Filters: {
        ...Eva.DEFAULT_SEARCH_PRODUCTS_REQUEST.Filters,
        product_id: {
          Values: parentIds,
        },
      },
    });

    return response!;
  }
  async getProductsByCategory(
    category: string,
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const response = await this.getService(SvcSearchProducts, {
      ...Eva.DEFAULT_SEARCH_PRODUCTS_REQUEST,
      Page: page,
      PageSize: pageSize,
      Sort: sort,

      Filters: {
        ...Eva.DEFAULT_SEARCH_PRODUCTS_REQUEST.Filters,
        tags: {
          Values: [category],
        },
        logical_level: {
          Values: ["root"],
          IncludeMissing: true,
        },
      },
    });

    return response!;
  }
  async getProductsByQuery(
    query: string,
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const response = await this.getService(SvcSearchProducts, {
      ...Eva.DEFAULT_SEARCH_PRODUCTS_REQUEST,
      Page: page,
      PageSize: pageSize,
      Sort: sort,

      Query: query,

      Filters: {
        ...Eva.DEFAULT_SEARCH_PRODUCTS_REQUEST.Filters,
        logical_level: {
          Values: ["root"],
          IncludeMissing: true,
        },
      },
    });

    return response!;
  }
  async getProductsBySet(
    setId: string,
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const response = await this.getService(SvcSearchProducts, {
      ...Eva.DEFAULT_SEARCH_PRODUCTS_REQUEST,
      Page: page,
      PageSize: pageSize,
      Sort: sort,

      ProductSearchTemplateID: setId,
    });

    return response!;
  }

  async getChildrenProducts(
    parentIds: string[],
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE,
    options?: { Options: SearchProducts["Options"] }
  ) {
    const response = await this.getService(SvcSearchProducts, {
      Page: page,
      PageSize: pageSize,
      Filters: {
        parent_product_ids: {
          Values: parentIds,
        },
      },
      Sort: [{ FieldName: "shade_number", Direction: 0 }],
      IncludedFields: Constants.QUERY_CHILDREN_INCLUDED_FIELDS,
      ...options,
    });

    return response!;
  }

  async getProductsFromBackendId(backendIds: string[]) {
    const response = await this.getService(SvcSearchProducts, {
      IncludedFields: ["slug"],
      Filters: {
        backend_id: {
          Values: backendIds,
          ExactMatch: true,
          IncludeMissing: false,
        },
      },
      Options: {
        AvailabilityOptions: {
          Delivery: {
            AvailabilityDate: true,
          },
          Pickup: {
            AvailabilityDate: false,
          },
        },
      },
    });

    return response!;
  }

  async getProductsFromFilters(filters: { [key: string]: SearchFilterModel }) {
    const response = await this.getService(SvcSearchProducts, {
      Filters: filters,
      IncludedFields: Constants.QUERY_INCLUDED_FIELDS,
    });

    return response!;
  }

  /**
   * @see https://dora.on-eva.io/ListOrganizationUnitSummaries
   * @see https://docs.newblack.io/documentation/dev/reference/list-organization-unit-summaries
   */
  async listOrganizationUnitSummaries() {
    const response = await this.getService(SvcListOrganizationUnitSummaries, {
      PageConfig: {
        Filter: {
          TypeID: 2,
          OrganizationUnitSetID: process.env.NEXT_PUBLIC_EVA_ORGANIZATION_SET_ID!,
        },
      },
    });

    return response?.Result?.Page ?? [];
  }

  /**
   * @see https://dora.on-eva.io/GetApplicationConfiguration
   * @see https://docs.newblack.io/documentation/dev/reference/get-application-configuration#getapplicationconfiguration
   */
  async getApplicationConfiguration() {
    const response = await this.getService(SvcGetApplicationConfiguration);

    return response!.Configuration as unknown as KIKOApplicationConfiguration;
  }

  async setShippingMethod(orderId: string, shippingMethodId: string) {
    return await this.getService(SvcSetShippingMethod, {
      OrderID: orderId,
      ShippingMethodID: shippingMethodId,
    });
  }

  async getProductAvailabilityDeliveryOnly(products: StockGetProductAvailability_Product[] | OrdersDtoOrderLineDto[]) {
    const productAvailabilityPayload: GetProductAvailability = {
      Products: products,
      Options: {
        Delivery: {
          AvailabilityDate: true,
        },
      },
    };

    const response = await this.getService(SvcGetProductAvailability, productAvailabilityPayload);

    return response;
  }

  async getProductAvailability(
    products: StockGetProductAvailability_Product[] | OrdersDtoOrderLineDto[],
    includeStores = true,
    availablesOnly = false,
    userLocation?: Coords
  ) {
    let productAvailabilityPayload: GetProductAvailability = {
      Products: products,
      Options: {
        Pickup: {
          IncludePickupOrganizationUnits: includeStores,
          IncludeOrganizationUnitsWithoutStock: !availablesOnly,
        },
        Delivery: {
          AvailabilityDate: true,
        },
      },
    };
    if (userLocation && userLocation.lat && userLocation.lng) {
      productAvailabilityPayload.Options!.Pickup!.InRadius = {
        Center: {
          Latitude: userLocation?.lat,
          Longitude: userLocation?.lng,
        },
        CountryID: this.locale?.split("-")[1]?.toUpperCase(),
      };
    }

    const response = await this.getService(SvcGetProductAvailability, productAvailabilityPayload);

    return response;
  }

  /**
   * Retrieves data from a specific Eva service using the specified payload.
   *
   * @param serviceDefinition - An Eva service definition
   * @param payload - The payload to include in the request
   * @param preprocessFetch - Used to customize fetch request before sending
   * @returns the requested data
   */
  protected async getService<T extends IEvaServiceDefinition>(
    serviceDefinition: new () => T,
    payload?: T["request"],
    preprocessFetch?: (req: Request) => void
  ) {
    const executeCall = async (token?: string): Promise<Response> => {
      // TODO: important, does this bypass the cache as described here?
      // https://www.npmjs.com/package/@springtree/eva-sdk-core-service/v/6.0.0-beta.9
      const endpoint = new EvaEndpoint(process.env.NEXT_PUBLIC_EVA_BACKEND_URL!);

      const service = new EvaService(createServiceDefinition(serviceDefinition), endpoint);

      service.setRequest(payload);

      const fetchRequest = service.buildFetchRequest();
      fetchRequest.headers.set("EVA-IDs-Mode", "StringIDs");

      if (this.locale) {
        fetchRequest.headers.set("Accept-Language", this.locale);
      }

      const authToken = token || this.userToken;
      if (authToken) {
        fetchRequest.headers.set("Authorization", authToken);
      }

      if (this.organizationUnitId) {
        fetchRequest.headers.set("eva-requested-organizationunitid", this.organizationUnitId.toString());
      }

      if (typeof preprocessFetch === "function") {
        preprocessFetch(fetchRequest);
      }

      return await fetch(fetchRequest);
    };

    // First attempt with current token
    let res = await executeCall();

    // If 401 and we have a token refresh callback, attempt to refresh and retry
    if (res.status === 401 && this.onTokenExpired) {
      try {
        const newToken = await this.onTokenExpired();

        if (newToken) {
          this.userToken = newToken;

          // Retry the original call with the new token
          res = await executeCall(newToken);
        } else {
          Logger.instance.warn("Eva: Token refresh returned no token, failing with 401");
        }
      } catch (refreshError) {
        Logger.instance.error("Eva: Token refresh failed", refreshError);
        // Continue with the original 401 response
      }
    }

    const resClone = res.clone();

    return (await res.json().catch(async () => await resClone.text())) as T["response"];
  }

  /**
   * Retrieves the detail for the product with the given id
   * @param productId - The product id
   * @returns the detail for the requested product
   */
  async getProductDetail(productId: string): Promise<GetProductDetailResponse> {
    // convert productId to number, even if using strings with KIKO ids would be ideal
    const response = await this.getService(SvcGetProductDetail, { ID: productId });
    invariant(response, "Did not get response from `getProductDetail`");

    return response!;
  }

  async getProductDetailByBackendId(backendId: string) {
    const response = await this.getService(
      SvcGetProductDetail,
      {
        ID: backendId,
      },
      (req) => {
        req.headers.set("EVA-IDs-Mode", "ExternalIDs");
      }
    );

    return response!;
  }

  /**
   * This asynchronous function retrieves product information based on the provided product IDs.
   *
   * @async
   * @function getProducts
   * @param productIds - A single or an array of product IDs. These can be either numbers or strings.
   * @returns {Promise<GetProductsResponse>} Returns a promise that resolves to the response from the `getProducts` service. The response includes the backend_id of the products.
   * @throws Will throw an error if the response from `getProducts` is not received.
   */
  async getProducts(productIds: string | string[]): Promise<GetProductsResponse> {
    const idsAsArray = Array.isArray(productIds) ? [...productIds] : [productIds];
    const response = await this.getService(SvcGetProducts, {
      ProductIDs: idsAsArray,
      IncludedFields: ["backend_id"],
    });
    invariant(response, "Did not get response from `getProducts`");

    return response!;
  }

  /**
   * Retrieves the discounts eligible for a specific product that contains PromotionLabel
   * @param productId - The product id
   * @returns the array of filtered discounts
   */
  async getProductDiscounts(productId: string) {
    // convert productId to number, even if using strings with KIKO ids would be ideal
    const response = await this.getService(SvcPrefigureDiscounts, { ProductIDs: [productId] });
    invariant(response, "Did not get response from `PrefigureDiscounts`");

    return response.Results?.filter(
      (discount) => discount.PromotionLabel && !!discount.EligibleProductIDs?.find((id) => id == productId)
    ).map((d) => ({
      DiscountID: d.DiscountID,
      PromotionLabel: d.PromotionLabel,
    }));
  }

  /**
   * Retrieves the detail for the product with the given id, if it has a 'color' logical_level; retrieves its root otherwise
   *
   * @param productId - The product id
   * @returns the detail for the root of the requested product
   */
  async getRootProductDetail(productId: string) {
    const response = await this.getProductDetail(productId);
    if (response.Result?.logical_level === "color") {
      const rootProductId = (response.Result.logical_level_hierarchy as { name: string; product_id: string }[])?.find(
        (e) => e.name === "root"
      )?.product_id;

      if (!rootProductId) {
        return undefined;
      }

      return await this.getProductDetail(rootProductId);
    }

    return response!;
  }

  // TODO need to understand which approach to follow in order to get all the stores nearby a specific area
  async listShopsByProximity(data: any) {
    const response = await this.getService(SvcListShopsByProximity, {
      ...data,
      PageConfig: {
        Limit: 100,
      },
    });
    // TODO remove this once EVA fix the filter by country
    return response?.Result?.Page
      ? Object.values(response.Result.Page).filter(
          (store) => store?.Address?.CountryID == this.locale?.split("-")[1]?.toUpperCase()
        )
      : undefined;
  }
}
